import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Avatar as AvatarCompo,
  Box,
  IconButton,
  Link,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useMemo, useRef, useState } from "react";
import cookie from "react-cookies";
import { useTranslation } from "react-i18next";
import { getTenantInfo } from "../../api/tenant";
import { getURLDomain } from "../../common/utils";
import { RIMS_COOKIE_STG_CONSTANTS } from "../../constants";
import { useConfigStore } from "../../store/config";
import styles from "./index.module.scss";
import { useCookieData } from "../../hook/useCookieData";
import { getCachedBranding } from "../../common/branding";
import freyaManagerImg from "../../assets/images/app/freya-manager.svg";
import tenantManagerImg from "../../assets/images/app/tenant-manager.png";

interface AppItem {
  imgSrc: string;
  title: string;
  subTitle: string;
  display: boolean;
  href?: string;
}

/**
 * Styles per Avatar design handoff.
 * Card: 20×24, r6; section gaps 16
 * Avatar 80; avatar→name 16; name→email 9; email width 230
 * App icon 40; icon→text 16; title→sub 2
 * Username Bold 24/28; menu title Bold 18/28
 */
const PAD_Y = 20;
const PAD_X = 24;
const GAP = 16;
const AVATAR = 80;
const AVATAR_TEXT_GAP = 16;
const NAME_EMAIL_GAP = 9;
const APP_ICON = 40;
const ICON_TEXT = 16;
const EMAIL_WIDTH = 230;
const CONTENT_WIDTH = AVATAR + AVATAR_TEXT_GAP + EMAIL_WIDTH;
const DISPLAY_L2 = { fontSize: 24, fontWeight: 700, lineHeight: "28px" };
const HEADING_L2 = { fontSize: 18, fontWeight: 700, lineHeight: "28px" };
const DEFAULT_TEXT = { fontSize: 14, fontWeight: 400, lineHeight: "20px" };

const Avatar: React.FC<{
  onProfile?: VoidFunction;
  onAccount?: VoidFunction;
  onLogout?: VoidFunction;
}> = ({ onLogout, onProfile, onAccount }) => {
  const userId = cookie.load(RIMS_COOKIE_STG_CONSTANTS.USER_ID);
  const tenantId = cookie.load(RIMS_COOKIE_STG_CONSTANTS.USER_TENANT_ID);
  const URLDomain = getURLDomain();
  const { userInfo: cookieUserInfo } = useCookieData();
  let userInfo = {} as any;
  try {
    userInfo = cookieUserInfo ? JSON.parse(decodeURIComponent(atob(cookieUserInfo))) : {};
  } catch(error) {
    console.log(error)
  }
  const [dropOpen, setDropOpen] = useState(false);
  const avatarRef = useRef<HTMLElement>();
  const { t } = useTranslation();
  const [privilege, language] = useConfigStore((state) => [state.privilege, state.language]);

  const { data: tenantInfo } = useQuery(
    ["fetchTenantInfo"],
    () => getTenantInfo(tenantId),
    {
      retry: false,
      refetchOnWindowFocus: false,
      select: (response) => {
        const resData = response?.data;
        if (!resData) {
          return null;
        }
        return {
          ...resData
        };
      },
    }
  );

  const appList = useMemo(() => {
    const privilegeList = privilege?.map((item: any) => { return item.name });
    const brandingCache = getCachedBranding();
    const brandIdentity = brandingCache?.brandIdentity;
    const brandingFavicon = brandIdentity?.favicon;
    const brandingTitle = brandIdentity?.title;
    const list: AppItem[] = [
      {
        imgSrc: brandingFavicon && brandingFavicon !== '/favicon.svg' ? brandingFavicon : freyaManagerImg,
        title: brandingTitle && brandingTitle !== 'Freya Fusion' ? brandingTitle : t("appLauncher.app.title"),
        subTitle: t("appLauncher.app.subTitle"),
        href: `https://app.${URLDomain}`,
        display: true
      },
      {
        imgSrc: tenantManagerImg,
        title: t("appLauncher.tenant.title"),
        subTitle: t("appLauncher.tenant.subTitle"),
        href: `https://platform.${URLDomain}`,
        display: privilegeList?.includes('view_platform')
      },
    ];
    return list;
  }, [privilege, language]);

  const visibleApps = appList.filter((item) => item.display);

  return (
    <>
      <Box
        ref={avatarRef}
        onClick={() => setDropOpen((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          marginX: 1,
        }}
      >
        <Tooltip title={t("common.avatar")}>
          <AvatarCompo sizes="small" sx={{ backgroundColor: "white" }} className="avatar-component">
            <AccountCircleRoundedIcon className="avatar-icon"
              sx={{
                fontSize: "36px",
                color: "Highlight",
              }}
            />
          </AvatarCompo>
        </Tooltip>
      </Box>
      <Popover
        id="avatar-dropdown-popover"
        open={dropOpen}
        anchorEl={avatarRef.current}
        onClose={() => setDropOpen(false)}
        className="ff-profile-dropdown"
        PaperProps={{
          sx: { borderRadius: "6px", overflow: "hidden" },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Paper className={styles["profile-paper"]} sx={{ borderRadius: "6px" }}>
          {/* Single Figma card pad — all left edges share PAD_X */}
          <Box
            sx={{
              boxSizing: "border-box",
              width: CONTENT_WIDTH + PAD_X * 2,
              pt: `${PAD_Y}px`,
              pb: `${PAD_Y}px`,
              px: `${PAD_X}px`,
              display: "flex",
              flexDirection: "column",
              gap: `${GAP}px`,
            }}
          >
            {/* Header */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{ width: "100%", minWidth: 0 }}
            >
              <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                <Tooltip title={tenantInfo?.name ?? "Freyr Solutions"}>
                  <Typography
                    variant="subtitle1"
                    component="div"
                    noWrap
                    sx={{
                      ...DEFAULT_TEXT,
                      color: "#262626",
                    }}
                  >
                    {tenantInfo?.name ?? "Freyr Solutions"}
                  </Typography>
                </Tooltip>
              </Box>
              <Link
                component="button"
                underline="none"
                className={`hover-link-btn ${styles["sign-out"]}`}
                sx={{ flexShrink: 0 }}
                onClick={() => {
                  onLogout?.();
                  setDropOpen(false);
                }}
              >
                {t("common.signOut")}
              </Link>
            </Stack>

            {/* Profile — avatar left = PAD_X */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: `${AVATAR_TEXT_GAP}px`,
              }}
            >
              <AvatarCompo
                sx={{
                  width: AVATAR,
                  height: AVATAR,
                  fontSize: 32,
                  color: "black",
                  bgcolor: "#D9D9D9",
                  flexShrink: 0,
                }}
              >
                {`${userInfo?.firstName?.charAt(0)}${userInfo?.lastName?.charAt(0)}`}
              </AvatarCompo>
              <Stack sx={{ width: EMAIL_WIDTH, gap: `${NAME_EMAIL_GAP}px`, minWidth: 0 }}>
                <Tooltip title={`${userInfo?.firstName} ${userInfo?.lastName}`}>
                  <Typography
                    component="div"
                    sx={{
                      ...DISPLAY_L2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "#404040",
                    }}
                  >
                    {`${userInfo?.firstName} ${userInfo?.lastName}`}
                  </Typography>
                </Tooltip>
                <Tooltip title={userInfo?.email}>
                  <Typography
                    component="div"
                    sx={{
                      ...DEFAULT_TEXT,
                      width: EMAIL_WIDTH,
                      maxWidth: EMAIL_WIDTH,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "#595959",
                    }}
                  >
                    {userInfo?.email}
                  </Typography>
                </Tooltip>
              </Stack>
            </Box>

            {/* Apps — break out horizontally for full-row hover; content re-inset to PAD_X */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: `${GAP}px`,
                mx: `-${PAD_X}px`,
                width: `calc(100% + ${PAD_X * 2}px)`,
              }}
            >
              {visibleApps.map((item) => (
                <Box
                  key={item.title}
                  className={styles["app-row"]}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    item.href && window.open(item.href);
                    setDropOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      item.href && window.open(item.href);
                      setDropOpen(false);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: `${ICON_TEXT}px`,
                    boxSizing: "border-box",
                    px: `${PAD_X}px`,
                    py: 0,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={item.imgSrc}
                    alt=""
                    sx={{
                      width: APP_ICON,
                      height: APP_ICON,
                      objectFit: "contain",
                      flexShrink: 0,
                      display: "block",
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      component="div"
                      sx={{
                        ...HEADING_L2,
                        color: "#404040",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      component="div"
                      sx={{
                        ...DEFAULT_TEXT,
                        mt: "2px",
                        color: "#595959",
                      }}
                    >
                      {item.subTitle}
                    </Typography>
                  </Box>
                  <IconButton className="hover-action" tabIndex={-1}>
                    <OpenInNewIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default Avatar;
